import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import {
  AppDataPostTags,
  UNIQUE_CAMPAIGN_URL_TAG,
} from "../../../../shared/models/appData";
import { BrandCampaignAddContentDialogData } from "../../../../shared/models/brand-campaign-add-content-dialog-data";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCategory } from "../../../../shared/models/brandCampaignCategory";
import { BrandCampaignDetails } from "../../../../shared/models/brandCampaignDetail";
import { BrandCampaignFacebookPost } from "../../../../shared/models/brandCampaignFacebookPost";
import { CampaignPostContentType } from "../../../../shared/enums/campaignPost.enums";
import { AppDataService } from "../../../../shared/services/api/app-data.service";
import { NotificationService } from "../../../../shared/services/notification.service";
import { BrandCampaignPostService } from "../../shared/services/brand-campaign-post.service";
import { CustomFormValidators } from "../../../../shared/validators/custom-form-validators";
import { BrandCampaignContentFormPostSuggestedTextComponent } from "../brand-campaign-content-form-post-text/brand-campaign-content-form-post-suggested-text.component";

export interface CampaignContentFormLinkPostDialogData
  extends BrandCampaignAddContentDialogData {
  readonly post?: BrandCampaignFacebookPost;
}

export type CampaignContentFormLinkPostDialogResult = undefined;

const HTTPS = "https://";
const DEFAULT_IMAGE_URL =
  "https://social-pals-prod.fra1.digitaloceanspaces.com/campaigns/1/50b85f0d-5526-482b-888e-52a410695466.png";

@Component({
  selector: "app-campaign-content-form-link-post-dialog",
  templateUrl: "./campaign-content-form-link-post-dialog.component.html",
  styleUrl: "./campaign-content-form-link-post-dialog.component.scss",
})
export class CampaignContentFormLinkPostDialogComponent
  implements OnInit, OnDestroy
{
  @ViewChild("suggestedText")
  protected suggestedText?: BrandCampaignContentFormPostSuggestedTextComponent;

  protected readonly campaign: BrandCampaign;
  protected readonly isNewPost: boolean;
  protected form: UntypedFormGroup;
  protected imageUrl: string;
  protected isSaveDisabled = false;
  protected loadingMetadata = false;
  protected postTags?: AppDataPostTags[];
  protected postText = "";
  protected saving = false;

  private readonly campaignDetails: BrandCampaignDetails;
  private readonly category: BrandCampaignCategory;
  private readonly subscriptions = new Subscription();
  private post: BrandCampaignFacebookPost;
  private postLink: string;

  constructor(
    private readonly brandCampaignPostService: BrandCampaignPostService,
    private readonly notificationService: NotificationService,
    private readonly dialogRef: MatDialogRef<
      CampaignContentFormLinkPostDialogComponent,
      CampaignContentFormLinkPostDialogResult
    >,
    private readonly appDataService: AppDataService,
    @Inject(MAT_DIALOG_DATA) data: CampaignContentFormLinkPostDialogData,
  ) {
    this.campaign = data.campaign;
    this.campaignDetails = data.campaignDetails;
    this.category = data.category;
    this.isNewPost = !data.post;

    if (data.post) {
      this.post = data.post;
      this.postLink = data.post.link.replace(HTTPS, "");
      this.postText = data.post.text;
      this.imageUrl = (data.post.image as string) || DEFAULT_IMAGE_URL;
    } else {
      this.imageUrl = DEFAULT_IMAGE_URL;
      this.post = BrandCampaignFacebookPost.create(
        false,
        CampaignPostContentType.LinkPost,
      );
      this.postLink = UNIQUE_CAMPAIGN_URL_TAG;
    }

    this.form = new UntypedFormGroup({
      postLink: new UntypedFormControl(this.postLink),
    });
  }

  public ngOnInit(): void {
    this.setLinkValidators();

    this.subscriptions.add(
      this.form
        .get("postLink")!
        .valueChanges.pipe(debounceTime(500), distinctUntilChanged())
        .subscribe((value) => {
          this.setLinkValidators();

          if (this.form.get("postLink")!.valid) {
            this.setPostLink(value);
          }
        }),
    );

    this.appDataService.get().then((appData) => {
      this.postTags = appData.postTags ?? [];
    });

    this.checkSaveDisabled();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setLinkValidators(): void {
    if (this.isDefaultLink()) {
      this.form
        .get("postLink")!
        .setValidators([
          Validators.required,
          CustomFormValidators.noUrlProtocol,
        ]);
    } else {
      this.form.get("postLink")!.setValidators([
        Validators.required,
        // Validate URL without caring if it has or not a protocol
        CustomFormValidators.urlWithOptionalProtocol,
        // Validate protocol separately from the URL to display specific error messages
        CustomFormValidators.noUrlProtocol,
      ]);
    }
    this.form.get("postLink")!.updateValueAndValidity();
  }

  protected onSaveContent(): void {
    this.saving = true;
    this.isSaveDisabled = true;
    this.post.text = this.postText;
    this.post.hasLinkPostValidLink = !this.isDefaultLink();

    if (this.isNewPost) {
      this.post.link = this.getFullLink();
      this.createFileAndSave();
    } else if (this.post.link !== this.postLink) {
      // in case the link has changed
      // we upload a new image
      this.createFileAndSave();
    } else {
      this.post.link = this.getFullLink();
      this.savePost();
    }
  }

  private getFullLink(): string {
    return HTTPS + this.postLink;
  }

  private createPost(): void {
    this.brandCampaignPostService
      .createCampaignDetailCategoryPost(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
        this.post,
      )
      .subscribe({
        next: (post) => {
          this.post = post;
          this.category.posts.push(this.post);
          this.dialogRef.close();
          this.notificationService.success("shared.changesSavedSuccessfully");
        },
        error: () => {
          this.saving = false;
          this.notificationService.error("shared.changesCouldNotBeSaved");
        },
      });
  }

  private savePost(): void {
    this.brandCampaignPostService
      .updateCampaignDetailCategoryPost(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
        this.post,
      )
      .subscribe({
        next: (post) => {
          this.post = post;
          this.category.posts = this.category.posts.map((catPost) =>
            catPost.id === post.id ? post : catPost,
          );
          this.dialogRef.close();
          this.notificationService.success("shared.changesSavedSuccessfully");
        },
        error: () => {
          this.saving = false;
          this.isSaveDisabled = false;
          this.notificationService.error("shared.changesCouldNotBeSaved");
        },
      });
  }

  private createFileAndSave(): void {
    if (this.isDefaultLink()) {
      if (this.isNewPost) {
        this.createPost();
      } else {
        this.savePost();
      }
      return;
    }

    this.brandCampaignPostService
      .createPostImageFromUrl(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
        this.imageUrl,
      )
      .subscribe({
        next: (image) => {
          this.post.imageFileId = image.id;
          if (this.isNewPost) {
            this.createPost();
          } else {
            this.savePost();
          }
        },
        error: () => {
          this.isSaveDisabled = false;
          this.saving = false;
          this.notificationService.error("shared.changesCouldNotBeSaved");
        },
      });
  }

  private getLinkMetadata(): void {
    this.loadingMetadata = true;

    this.brandCampaignPostService
      .getLinkMetadata(this.getFullLink())
      .subscribe({
        next: (webLinkMetadata) => {
          this.loadingMetadata = false;
          this.imageUrl = webLinkMetadata.imageUrl;

          if (!this.postText) {
            this.postText = webLinkMetadata.description;
          }

          this.checkSaveDisabled();
        },
        error: () => {
          this.imageUrl = DEFAULT_IMAGE_URL;
          this.loadingMetadata = false;
          this.checkSaveDisabled();
        },
      });
  }

  private isDefaultLink(): boolean {
    return this.form.get("postLink")!.value === UNIQUE_CAMPAIGN_URL_TAG;
  }

  private setPostLink(postLink: string): void {
    this.postLink = postLink;

    if (this.isDefaultLink()) {
      this.imageUrl = DEFAULT_IMAGE_URL;
      this.checkSaveDisabled();
    } else {
      this.getLinkMetadata();
    }
  }

  protected setPostText(setPostText: string): void {
    this.postText = setPostText;
    this.checkSaveDisabled();
  }

  private checkSaveDisabled(): void {
    if (this.form?.invalid || !this.postText || this.saving) {
      this.isSaveDisabled = true;
      return;
    }

    this.isSaveDisabled = !this.suggestedText?.isValid;
  }
}
